// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A fingertip through a callout (M13-17).
//
// A callout is a small face hanging `CALLOUT_HEIGHT` over the part its complaint is about, and
// it is tapped as a tile is (`tap.ts`): a fingertip pushed down through it, once, on the edge.
// The tap selects the part — the Builder's click on a complaint — and says the complaint's own
// sentence. Nothing is written; a selection is not an edit.
//
// **In the playfield's frame, scaled to the room.** The bench is drawn at a scale, and a
// callout's face has to be the size of a fingertip in the *room* whatever the scale, or a bench
// at 0.33 would have callouts the size of grains. So the spots arrive in playfield metres (where
// the picture drew them), the hands arrive in playfield metres (as the carry's hands do), and
// this puts each hand into each callout's own frame in room metres — the frame `TapGate`
// measures its twelve millimetres in. +z is toward the author: up off the surface.
//
// **Only an open hand, only off the tray, only while nothing is carried.** A hand carrying a
// part passes over callouts with its fingertip a centimetre from the pinch, and a pinched hand
// at a plinth is taking a part; neither is a tap. Pure: no Three, no clock. `CalloutTaps.cs` is
// this file.

import { TapGate, type TapState } from './tap'
import type { CarryHand } from './partCarry'

/** How high a callout hangs over the surface, room metres: above a part, under a hand. */
export const CALLOUT_HEIGHT = 0.045
/** A callout's face, room metres, square: a fingertip and a half. */
export const CALLOUT_FACE = 0.032

/** Where a callout is, in playfield metres, by the key the picture drew it under. */
export interface CalloutSpot {
  readonly key: string
  readonly x: number
  readonly y: number
  /** How far over the surface the place is, playfield metres (M13-24): a ramp's callout hangs over the ramp. Absent is the wood. */
  readonly lift?: number
}

const FACE = { width: CALLOUT_FACE, height: CALLOUT_FACE }

export class CalloutTaps {
  private readonly gates = new Map<string, Record<'left' | 'right', TapGate>>()

  /**
   * One frame: the callouts on the bench, both hands in the playfield's frame, and the scale
   * the bench is drawn at. Answers the key of the callout tapped this frame, or nothing.
   * `carrying` is true while a part is in either hand: no callout is tapped then.
   */
  advance(spots: readonly CalloutSpot[], hands: readonly CarryHand[], scale: number, carrying: boolean): string | undefined {
    // Gates for callouts that are gone are dropped, so a callout that comes back starts clean.
    const live = new Set(spots.map((spot) => spot.key))
    for (const key of [...this.gates.keys()]) if (!live.has(key)) this.gates.delete(key)

    let tapped: string | undefined
    for (const spot of spots) {
      let gates = this.gates.get(spot.key)
      if (gates === undefined) {
        gates = { left: new TapGate(FACE), right: new TapGate(FACE) }
        this.gates.set(spot.key, gates)
      }
      for (const hand of hands) {
        const gate = gates[hand.handedness]
        let state: TapState
        if (!hand.present || carrying || hand.grab || hand.atPlinth !== undefined) {
          state = gate.release()
        } else {
          state = gate.advance({
            x: (hand.x - spot.x) * scale,
            y: (hand.y - spot.y) * scale,
            z: (hand.z - (spot.lift ?? 0)) * scale - CALLOUT_HEIGHT,
          })
        }
        if (state.pressedEdge && tapped === undefined) tapped = spot.key
      }
    }
    return tapped
  }

  /** Every gate let go — the mode is leaving, the hands were reassigned. */
  release(): void {
    for (const gates of this.gates.values()) {
      gates.left.release()
      gates.right.release()
    }
    this.gates.clear()
  }
}
